/**
 * Total Balance Composition diagnostic card.
 *
 * Per bank_accounts row contributing to the dashboard's Bank Balance stat
 * card (group scope): account name, bank, source, entity, native balance +
 * currency, converted GBP amount, a provenance tag (the stored "source"
 * field, or "untagged"), last transaction date, and a mock-data-audit
 * verdict (mock / real / quarantined / unclassifiable). Sorted by converted
 * amount descending, with the summed total at top so it reconciles against
 * the stat card.
 *
 * Read-only. No writes, no purge changes here.
 */

import { useState } from 'react';
import { Loader2, Wallet } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

var VERDICT_META = {
  mock: { label: 'Mock', className: 'text-red-700 border-red-300' },
  real: { label: 'Real', className: 'text-green-700 border-green-300' },
  quarantined: { label: 'Quarantined', className: 'text-amber-700 border-amber-300' },
  unclassifiable: { label: 'Unclassifiable', className: 'text-purple-700 border-purple-300' },
};

function VerdictBadge({ verdict }) {
  var meta = VERDICT_META[verdict] || { label: verdict || '—', className: 'text-muted-foreground' };
  return (
    <Badge variant="outline" className={meta.className} data-testid={'balance-verdict-' + verdict}>
      {meta.label}
    </Badge>
  );
}

function fmtTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return iso;
  }
}

function fmtMoney(str) {
  if (str == null) return '—';
  var n = Number(str);
  if (Number.isNaN(n)) return str;
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function TotalBalanceCompositionPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);

  var run = async function () {
    setLoading(true);
    setData(null);
    try {
      var res = await diagnosticsAPI.totalBalanceComposition();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="total-balance-composition-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Wallet className="h-5 w-5 text-muted-foreground" />
          Total Balance Composition
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Every bank_accounts row contributing to the dashboard's Bank Balance
          card (group scope), with its provenance tag and mock-data-audit
          verdict, so the card's total can be reconciled account-by-account.
        </p>

        <Button onClick={run} disabled={loading} data-testid="run-total-balance-composition">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wallet className="h-4 w-4 mr-2" />}
          Run Diagnostic
        </Button>

        {data && (
          <div className="space-y-4">
            <div className="rounded-md px-3 py-2 text-sm bg-muted/50 border" data-testid="total-balance-summary">
              <div className="font-medium">
                Total (converted, GBP): {fmtMoney(data.total_converted_gbp)} across {data.account_count} accounts
              </div>
              {data.unconvertible_currencies.length > 0 && (
                <div className="text-amber-700 text-xs mt-1">
                  Unconvertible currencies (excluded from total, not zeroed): {data.unconvertible_currencies.join(', ')}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-1">{data.current_computation_path}</div>
            </div>

            <div className="flex gap-2 flex-wrap">
              {Object.keys(data.verdict_counts).map(function (v) {
                return (
                  <Badge key={v} variant="outline" className={(VERDICT_META[v] || {}).className}>
                    {data.verdict_counts[v]} {(VERDICT_META[v] || {}).label || v}
                  </Badge>
                );
              })}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="total-balance-composition-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-1.5 pr-3 font-medium">Account</th>
                    <th className="py-1.5 pr-3 font-medium">Bank</th>
                    <th className="py-1.5 pr-3 font-medium">Source</th>
                    <th className="py-1.5 pr-3 font-medium">Entity</th>
                    <th className="py-1.5 pr-3 font-medium">Native balance</th>
                    <th className="py-1.5 pr-3 font-medium">Converted (GBP)</th>
                    <th className="py-1.5 pr-3 font-medium">Provenance</th>
                    <th className="py-1.5 pr-3 font-medium">Last tx date</th>
                    <th className="py-1.5 pr-3 font-medium">Verdict</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(function (r) {
                    return (
                      <tr key={r.account_id} className="border-b border-border/50 align-top">
                        <td className="py-1.5 pr-3">
                          {r.account_name || r.account_id}
                          {r.is_duplicate && (
                            <span className="ml-1 text-[10px] text-muted-foreground">(dup group)</span>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">{r.bank_name || '—'}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs">{r.source || '—'}</td>
                        <td className="py-1.5 pr-3">
                          {r.entity_name}
                          {!r.entity_resolved && <span className="ml-1 text-[10px] text-amber-700">(unresolved)</span>}
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-xs">
                          {fmtMoney(r.native_balance)} {r.native_currency}
                        </td>
                        <td className="py-1.5 pr-3 font-mono text-xs">
                          {r.converted_gbp != null ? fmtMoney(r.converted_gbp) : (
                            <span className="text-amber-700">unconvertible</span>
                          )}
                        </td>
                        <td className="py-1.5 pr-3">
                          <Badge variant="outline" className={r.provenance === 'untagged' ? 'text-red-700 border-red-300' : ''}>
                            {r.provenance}
                          </Badge>
                        </td>
                        <td className="py-1.5 pr-3 text-xs">{fmtTs(r.last_transaction_date)}</td>
                        <td className="py-1.5 pr-3">
                          <VerdictBadge verdict={r.verdict} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div>
              <div className="text-xs font-medium mb-1">Bridge orphan census</div>
              <p className="text-[11px] text-muted-foreground mb-2">
                Bridge-source accounts/transactions, cross-checked against db.bridge_connections.
                Zero connections with bridge-source data present means this was not written by a
                live Bridge sync in this environment.
              </p>
              {(function () {
                var b = data.bridge_orphan_census || {};
                return (
                  <div className="space-y-2" data-testid="bridge-orphan-census">
                    {b.headline && (
                      <div className="rounded-md px-3 py-2 text-sm bg-amber-50 border border-amber-200 text-amber-900">
                        {b.headline}
                      </div>
                    )}
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline">{b.bridge_connections_configured} Bridge connection(s)</Badge>
                      <Badge variant="outline">{b.bridge_source_transaction_count} bridge-source transaction(s)</Badge>
                      <Badge variant="outline">
                        counterparty fill: {b.counterparty_fill_rate == null ? '—' : Math.round(b.counterparty_fill_rate * 100) + '%'}
                      </Badge>
                      <Badge variant="outline">
                        date range: {b.date_range && b.date_range.earliest ? b.date_range.earliest + ' → ' + b.date_range.latest : '—'}
                      </Badge>
                    </div>
                    {b.transaction_id_shape && b.transaction_id_shape.dense_consecutive_small_integers && (
                      <div className="text-xs text-amber-700">
                        Transaction ids are dense, small, consecutive integers ({b.transaction_id_shape.numeric_suffixes.join(', ')})
                        -- a shape typical of seed/demo data, not provider-assigned ids.
                      </div>
                    )}
                    {b.account_id_shape && b.account_id_shape.dense_consecutive_small_integers && (
                      <div className="text-xs text-amber-700">
                        Account ids are dense, small, consecutive integers ({b.account_id_shape.numeric_suffixes.join(', ')})
                        -- same seed/demo-data signal.
                      </div>
                    )}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="bridge-orphan-accounts-table">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="py-1.5 pr-3 font-medium">Account</th>
                            <th className="py-1.5 pr-3 font-medium">Entity</th>
                            <th className="py-1.5 pr-3 font-medium">Hidden</th>
                            <th className="py-1.5 pr-3 font-medium">Provenance tag</th>
                            <th className="py-1.5 pr-3 font-medium">bridge_connection field</th>
                            <th className="py-1.5 pr-3 font-medium">Last tx date</th>
                            <th className="py-1.5 pr-3 font-medium">Mock-audit verdict</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(b.accounts || []).map(function (a) {
                            return (
                              <tr key={a.account_id} className="border-b border-border/50">
                                <td className="py-1.5 pr-3">
                                  <div>{a.account_name || a.account_id}</div>
                                  <div className="font-mono text-[10px] text-muted-foreground">{a.account_id}</div>
                                </td>
                                <td className="py-1.5 pr-3 font-mono text-xs">{a.entity_id || '—'}</td>
                                <td className="py-1.5 pr-3">
                                  {a.hidden ? (
                                    <Badge variant="outline" className="text-muted-foreground">hidden</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-green-700 border-green-300">visible</Badge>
                                  )}
                                </td>
                                <td className="py-1.5 pr-3">
                                  {a.provenance_tag_present ? (
                                    <Badge variant="outline" className="text-green-700 border-green-300">present</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-red-700 border-red-300">absent</Badge>
                                  )}
                                </td>
                                <td className="py-1.5 pr-3">
                                  {a.bridge_connection_field_present ? (
                                    <Badge variant="outline" className="text-green-700 border-green-300">present</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-red-700 border-red-300">absent</Badge>
                                  )}
                                </td>
                                <td className="py-1.5 pr-3 text-xs">{fmtTs(a.last_transaction_date)}</td>
                                <td className="py-1.5 pr-3">
                                  <VerdictBadge verdict={a.mock_audit_verdict} />
                                </td>
                              </tr>
                            );
                          })}
                          {(!b.accounts || b.accounts.length === 0) && (
                            <tr>
                              <td className="py-1.5 pr-3 text-muted-foreground" colSpan={7}>No bridge-source accounts found.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
